import React, { useState, useEffect, useRef } from 'react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { GOOGLE_MAPS_API_KEY } from 'config';
import './_googleLocationPicker.scss';

const GoogleLocationPicker = ({ 
  value, 
  onChange, 
  disabled = false,
  apiKey = null 
}) => {
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [map, setMap] = useState(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // Load Google Maps script
  useEffect(() => {
    const getApiKey = () => {
      if (apiKey) return apiKey;
      if (GOOGLE_MAPS_API_KEY) return GOOGLE_MAPS_API_KEY;
      return process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
    };

    const apiKeyValue = getApiKey();

    if (!apiKeyValue) {
      console.warn('Google Maps API key is not configured');
      return;
    }

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      setMapsLoaded(true);
      return;
    }

    // Load Google Maps script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKeyValue}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setMapsLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load Google Maps script');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, [apiKey]);

  // Initialize map when component mounts and maps are loaded (only once)
  useEffect(() => {
    if (!mapRef.current || !mapsLoaded || !window.google || map) return;

    // Pakistan bounds
    const pakistanBounds = new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(23.6345, 60.8726), // Southwest corner
      new window.google.maps.LatLng(36.7750, 77.8375)  // Northeast corner
    );

    const initialCenter = value?.latitude && value?.longitude 
      ? { lat: parseFloat(value.latitude), lng: parseFloat(value.longitude) }
      : { lat: 30.3753, lng: 69.3451 }; // Default to center of Pakistan

    const googleMap = new window.google.maps.Map(mapRef.current, {
      center: initialCenter,
      zoom: value?.latitude && value?.longitude ? 15 : 6,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      restriction: {
        latLngBounds: pakistanBounds,
        strictBounds: false, // Allow slight overflow for better UX
      },
    });

    setMap(googleMap);

    // Add click listener to map
    googleMap.addListener('click', (e) => {
      if (disabled) return;
      
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      
      // Update marker position
      if (markerRef.current) {
        markerRef.current.setPosition({ lat, lng });
      } else {
        const newMarker = new window.google.maps.Marker({
          position: { lat, lng },
          map: googleMap,
          draggable: !disabled,
        });
        markerRef.current = newMarker;
      }

      // Reverse geocode to get address
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        console.log(results);
        if (status === 'OK' && results[0]) {
          const addressComponents = results[0].address_components;
          let city = '';
          
          // Extract city from address components
          for (const component of addressComponents) {
            if (component.types.includes('locality') || component.types.includes('administrative_area_level_2')) {
              city = component.long_name;
              break;
            }
          }

          onChange({
            address: results[0].formatted_address,
            city: city || value?.city || '',
            latitude: lat,
            longitude: lng,
            place_id: value?.place_id || null, // Preserve existing place_id if available
          });
        } else {
          // If geocoding fails, still update coordinates
          onChange({
            address: value?.address || '',
            city: value?.city || '',
            latitude: lat,
            longitude: lng,
            place_id: value?.place_id || null, // Preserve existing place_id if available
          });
        }
      });
    });

    // If we have existing coordinates, add marker immediately
    if (value?.latitude && value?.longitude) {
      const existingMarker = new window.google.maps.Marker({
        position: { 
          lat: parseFloat(value.latitude), 
          lng: parseFloat(value.longitude) 
        },
        map: googleMap,
        draggable: !disabled,
      });

      existingMarker.addListener('dragend', (e) => {
        if (disabled) return;
        
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const addressComponents = results[0].address_components;
            let city = '';
            
            for (const component of addressComponents) {
              if (component.types.includes('locality') || component.types.includes('administrative_area_level_2')) {
                city = component.long_name;
                break;
              }
            }

            onChange({
              address: results[0].formatted_address,
              city: city || value?.city || '',
              latitude: lat,
              longitude: lng,
              place_id: value?.place_id || null, // Preserve existing place_id if available
            });
          }
        });
      });

      markerRef.current = existingMarker;
      
      // Center map on marker
      googleMap.setCenter({ 
        lat: parseFloat(value.latitude), 
        lng: parseFloat(value.longitude) 
      });
      googleMap.setZoom(15);
    }
  }, [mapsLoaded, map, disabled]);

  // Cleanup marker on unmount
  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
    };
  }, []);

  // Update map center and marker when value changes externally
  useEffect(() => {
    if (!map || !mapsLoaded) return;
    
    if (value?.latitude && value?.longitude) {
      const lat = parseFloat(value.latitude);
      const lng = parseFloat(value.longitude);
      
      // Create marker if it doesn't exist
      if (!markerRef.current) {
        const newMarker = new window.google.maps.Marker({
          position: { lat, lng },
          map: map,
          draggable: !disabled,
        });
        
        newMarker.addListener('dragend', (e) => {
          if (disabled) return;
          
          const dragLat = e.latLng.lat();
          const dragLng = e.latLng.lng();
          
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat: dragLat, lng: dragLng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const addressComponents = results[0].address_components;
              let city = '';
              
              for (const component of addressComponents) {
                if (component.types.includes('locality') || component.types.includes('administrative_area_level_2')) {
                  city = component.long_name;
                  break;
                }
              }

              onChange({
                address: results[0].formatted_address,
                city: city || value?.city || '',
                latitude: dragLat,
                longitude: dragLng,
                place_id: value?.place_id || null,
              });
            }
          });
        });
        
        markerRef.current = newMarker;
      } else {
        // Update existing marker position
        markerRef.current.setPosition({ lat, lng });
      }
      
      // Center map on marker
      map.setCenter({ lat, lng });
      map.setZoom(15);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, mapsLoaded, value?.latitude, value?.longitude, disabled]);

  const handlePlaceSelect = (place) => {
    if (disabled || !mapsLoaded) return;
    
    setSelectedPlace(place);
    
    if (!place || !place.value) return;

    // Get place details
    const service = new window.google.maps.places.PlacesService(map || document.createElement('div'));
    const placeId = place.value.place_id;

    const request = {
      placeId: placeId,
      fields: ['formatted_address', 'geometry', 'address_components', 'name'],
    };

    service.getDetails(request, (placeDetails, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && placeDetails) {
        const lat = placeDetails.geometry.location.lat();
        const lng = placeDetails.geometry.location.lng();
        
        // Extract city from address components
        let city = '';
        const addressComponents = placeDetails.address_components || [];
        for (const component of addressComponents) {
          if (component.types.includes('locality') || component.types.includes('administrative_area_level_2')) {
            city = component.long_name;
            break;
          }
        }

        // Update form values
        onChange({
          address: placeDetails.formatted_address || placeDetails.name || '',
          city: city || value?.city || '',
          latitude: lat,
          longitude: lng,
          place_id: placeId,
        });

        // Update map
        if (map) {
          map.setCenter({ lat, lng });
          map.setZoom(15);

          if (markerRef.current) {
            markerRef.current.setPosition({ lat, lng });
          } else {
            const newMarker = new window.google.maps.Marker({
              position: { lat, lng },
              map: map,
              draggable: !disabled,
            });
            markerRef.current = newMarker;
          }
        }
      }
    });
  };

  // Get API key from props, config, or environment
  const getApiKey = () => {
    if (apiKey) return apiKey;
    if (GOOGLE_MAPS_API_KEY) return GOOGLE_MAPS_API_KEY;
    return process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  };

  const apiKeyValue = getApiKey();

  if (!apiKeyValue) {
    return (
      <div className="google-location-picker-error">
        <p>Google Maps API key is not configured. Please add REACT_APP_GOOGLE_MAPS_API_KEY to your environment variables or configure GOOGLE_MAPS_API_KEY in config.js</p>
      </div>
    );
  }

  if (!mapsLoaded) {
    return (
      <div className="google-location-picker-loading">
        <p>Loading Google Maps...</p>
      </div>
    );
  }

  return (
    <div className="google-location-picker">
      <div className="location-search">
        <label>Search Location</label>
        <GooglePlacesAutocomplete
          apiKey={apiKeyValue}
          selectProps={{
            value: selectedPlace,
            onChange: handlePlaceSelect,
            placeholder: 'Search for a location...',
            isDisabled: disabled,
            styles: {
              control: (provided) => ({
                ...provided,
                minHeight: '38px',
                borderColor: '#ced4da',
                '&:hover': {
                  borderColor: '#ced4da',
                },
              }),
              placeholder: (provided) => ({
                ...provided,
                color: '#6c757d',
              }),
            },
          }}
          autocompletionRequest={{
            componentRestrictions: { country: 'pk' }, // Restrict to Pakistan
          }}
        />
      </div>

      <div className="location-map-container">
        <label>Pick Location on Map</label>
        <div className="map-instructions">
          <p>Click on the map to set the location, or drag the marker to adjust</p>
        </div>
        <div ref={mapRef} className="location-map" />
      </div>

      {value && (value.latitude || value.longitude) && (
        <div className="location-coordinates">
          <div className="coordinate-item">
            <span className="coordinate-label">Latitude:</span>
            <span className="coordinate-value">{parseFloat(value.latitude || 0).toFixed(6)}</span>
          </div>
          <div className="coordinate-item">
            <span className="coordinate-label">Longitude:</span>
            <span className="coordinate-value">{parseFloat(value.longitude || 0).toFixed(6)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleLocationPicker;

